import React, { useEffect, useMemo, useState } from 'react'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, InputAdornment, Typography, CircularProgress, Alert } from '@mui/material'
import { useParams } from 'react-router-dom'
import { Search as SearchIcon } from 'lucide-react'
import ActionBar from '../../components/ActionBar'
import RefreshButton from '../../components/RefreshButton'
import ExportButton from '../../components/ExportButton'
import { gridApi } from '../../api/grid'
import { useAuth } from '../../contexts/AuthContext'

type Column = { key: string; label: string }

type BasicaConfig = {
  title: string
  palette: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'
  columns: Column[]
  rows: any[]
}

const CONFIGS: Record<string, BasicaConfig> = {
  'cfop': {
    title: 'CFOP',
    palette: 'primary',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'descricao', label: 'DESCRIÇÃO' },
      { key: 'ativo', label: 'ATIVO' },
      { key: 'ultimo_update', label: 'ATUALIZADO EM' },
    ],
    rows: Array.from({ length: 52 }).map((_, i) => ({
      id: i + 1,
      codigo: String(5100 + i),
      descricao: `Operação fiscal ${5100 + i}`,
      ativo: i % 3 !== 0 ? 'Sim' : 'Não',
      ultimo_update: '2026-02-14',
    })),
  },
  'ncm': {
    title: 'NCM',
    palette: 'info',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'descricao', label: 'DESCRIÇÃO' },
      { key: 'aliq_nac', label: 'ALÍQ. NAC.' },
      { key: 'aliq_imp', label: 'ALÍQ. IMP.' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo: '0101.21.00', descricao: 'Bovinos reprodutores', aliq_nac: '8%', aliq_imp: '12%', ativo: 'Sim' },
      { id: 2, codigo: '0207.12.00', descricao: 'Carnes de aves', aliq_nac: '8%', aliq_imp: '16%', ativo: 'Sim' },
    ],
  },
  'cst-icms': {
    title: 'CST ICMS',
    palette: 'secondary',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'descricao', label: 'DESCRIÇÃO' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo: '00', descricao: 'Tributação integral', ativo: 'Sim' },
      { id: 2, codigo: '20', descricao: 'Base de cálculo reduzida', ativo: 'Sim' },
    ],
  },
  'cst-pis-cofins': {
    title: 'CST PIS/COFINS',
    palette: 'error',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'descricao', label: 'DESCRIÇÃO' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo: '01', descricao: 'Operação tributável com alíquota básica', ativo: 'Sim' },
      { id: 2, codigo: '04', descricao: 'Operação tributável monofásica', ativo: 'Sim' },
    ],
  },
  'unidades': {
    title: 'Unidades',
    palette: 'success',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'descricao', label: 'DESCRIÇÃO' },
      { key: 'simbolo', label: 'SÍMBOLO' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo: 'UN', descricao: 'Unidade', simbolo: 'un', ativo: 'Sim' },
      { id: 2, codigo: 'KG', descricao: 'Quilograma', simbolo: 'kg', ativo: 'Sim' },
    ],
  },
  'estados': {
    title: 'Estados',
    palette: 'warning',
    columns: [
      { key: 'uf', label: 'UF' },
      { key: 'nome', label: 'NOME' },
      { key: 'ibge', label: 'IBGE' },
      { key: 'regiao', label: 'REGIÃO' },
    ],
    rows: [
      { id: 1, uf: 'SP', nome: 'São Paulo', ibge: '35', regiao: 'Sudeste' },
      { id: 2, uf: 'RJ', nome: 'Rio de Janeiro', ibge: '33', regiao: 'Sudeste' },
    ],
  },
  'municipios': {
    title: 'Municipios',
    palette: 'secondary',
    columns: [
      { key: 'codigo_ibge', label: 'IBGE' },
      { key: 'nome', label: 'NOME' },
      { key: 'uf', label: 'UF' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo_ibge: '3550308', nome: 'São Paulo', uf: 'SP', ativo: 'Sim' },
      { id: 2, codigo_ibge: '3304557', nome: 'Rio de Janeiro', uf: 'RJ', ativo: 'Sim' },
    ],
  },
  'moedas': {
    title: 'Moedas',
    palette: 'info',
    columns: [
      { key: 'codigo', label: 'CÓDIGO' },
      { key: 'nome', label: 'NOME' },
      { key: 'simbolo', label: 'SÍMBOLO' },
      { key: 'pais', label: 'PAÍS' },
      { key: 'ativo', label: 'ATIVO' },
    ],
    rows: [
      { id: 1, codigo: 'BRL', nome: 'Real', simbolo: 'R$', pais: 'Brasil', ativo: 'Sim' },
      { id: 2, codigo: 'USD', nome: 'Dólar', simbolo: 'US$', pais: 'Estados Unidos', ativo: 'Sim' },
    ],
  },
  'mercados': {
    title: 'Mercados',
    palette: 'primary',
    columns: [
      { key: 'cdmerc', label: 'CÓDIGO' },
      { key: 'nmmerc', label: 'NOME' },
      { key: 'status', label: 'STATUS' },
    ],
    rows: [],
  },
}

const BasicaPage: React.FC = () => {
  const params = useParams<{ slug: string }>()
  const { getEmpresaId } = useAuth()
  const slug = params.slug || ''
  const config = CONFIGS[slug]
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dynamicRows, setDynamicRows] = useState<any[] | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  const empresaId = getEmpresaId()
  useEffect(() => {
    let ignore = false
    const tryLoad = async () => {
      setLoading(true)
      setError(null)
      setDynamicRows(null)
      try {
        const items = await gridApi.list(slug, { empresa_id: empresaId ?? undefined })
        if (!ignore && items) {
          setDynamicRows(items)
        }
      } catch (e: any) {
        if (!ignore) setError(e?.response?.data?.detail ?? e?.message ?? 'Erro ao carregar dados')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    if (slug) tryLoad()
    return () => { ignore = true }
  }, [slug, empresaId])

  const effectiveRows = dynamicRows ?? config?.rows ?? []
  const effectiveColumns: Column[] = useMemo(() => {
    if (config?.columns?.length) return config.columns
    if (dynamicRows && dynamicRows.length > 0) {
      const keys = Object.keys(dynamicRows[0])
      return keys
        .filter(k => !['id', 'created_at', 'updated_at'].includes(k))
        .slice(0, 8)
        .map(k => ({ key: k, label: k.toUpperCase() }))
    }
    return []
  }, [dynamicRows, config])

  const filteredRows = useMemo(() => {
    if (!searchTerm) return effectiveRows
    return effectiveRows.filter((r: any) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [effectiveRows, searchTerm])

  if (!config) {
    return (
      <Box p={3}>
        <Typography variant="h6">Tela não configurada</Typography>
      </Box>
    )
  }

  const paginated = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      <ActionBar>
        <Typography variant="h6" sx={{ color: (theme) => (theme.palette as any)[config.palette].main, fontWeight: 'bold', mr: 'auto' }}>
          {config.title}
        </Typography>
        <RefreshButton onClick={() => window.location.reload()} />
        <ExportButton
          data={filteredRows}
          columns={effectiveColumns.map((c) => ({ key: c.key, label: c.label }))}
          filename={`${slug}.xlsx`}
          sheetName={config.title}
        />
      </ActionBar>
      <Paper sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 320 }}>
        <Box p={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filtrar nesta tabela..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon size={18} /></InputAdornment>
            }}
          />
        </Box>
        {loading && (
          <Box p={3} display="flex" justifyContent="center" sx={{ color: (theme) => (theme.palette as any)[config.palette].main }}>
            <CircularProgress color="inherit" />
          </Box>
        )}
        {!loading && error && (
          <Box p={2}><Alert severity="error">{error}</Alert></Box>
        )}
        {!loading && !error && dynamicRows && dynamicRows.length === 0 && (
          <Box p={2}><Alert severity="info">Nenhum registro encontrado</Alert></Box>
        )}
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {effectiveColumns.map((c) => (
                  <TableCell key={c.key} sx={{ fontWeight: 'bold' }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((r, idx) => (
                <TableRow key={r.cdmerc ?? r.id ?? idx} hover>
                  {effectiveColumns.map((c) => (
                    <TableCell key={`${r.cdmerc ?? r.id ?? idx}-${c.key}`}>{String(r[c.key] ?? '')}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </Paper>
    </Box>
  )
}

export default BasicaPage
